import { ATLAS_FACES, ATLAS_CUTOUT_IDS } from "./atlas-faces.js";

(function () {
  const YEAR_MIN_GLOBAL = 1869;
  const YEAR_MAX_GLOBAL = 2026;
  const WINDOW = 18;
  const CLUSTER_CELL = 2.4;
  const CLUSTER_RADIUS = 1.35;
  const OPEN_YEAR = 1900;

  const ERA_RANGES = {
    all: { min: YEAR_MIN_GLOBAL, max: YEAR_MAX_GLOBAL, jump: YEAR_MAX_GLOBAL },
    pre1950: { min: YEAR_MIN_GLOBAL, max: 1949, jump: 1949 },
    "1950-1999": { min: 1950, max: 1999, jump: 1999 },
    "2000+": { min: 2000, max: YEAR_MAX_GLOBAL, jump: YEAR_MAX_GLOBAL },
  };

  const entries = Array.isArray(window.ATLAS_ENTRIES)
    ? window.ATLAS_ENTRIES.slice().sort(function (a, b) {
        return a.year - b.year || a.name.localeCompare(b.name);
      })
    : [];

  const els = {
    stage: document.querySelector(".atlas-stage"),
    map: document.getElementById("atlas-map"),
    markers: document.getElementById("atlas-markers"),
    empty: document.getElementById("atlas-empty"),
    card: document.getElementById("atlas-card"),
    cardYear: document.getElementById("atlas-card-year"),
    cardName: document.getElementById("atlas-card-name"),
    cardPlace: document.getElementById("atlas-card-place"),
    cardBlurb: document.getElementById("atlas-card-blurb"),
    cardLink: document.getElementById("atlas-card-link"),
    cardClose: document.getElementById("atlas-card-close"),
    yearReadout: document.getElementById("atlas-year-readout"),
    count: document.getElementById("atlas-count"),
    track: document.getElementById("atlas-track"),
    handle: document.getElementById("atlas-handle"),
    ticks: document.getElementById("atlas-ticks"),
    rangeMin: document.getElementById("atlas-range-min"),
    rangeMax: document.getElementById("atlas-range-max"),
    eras: document.querySelectorAll(".atlas-era"),
    note: document.getElementById("atlas-note"),
    noteWrap: document.getElementById("atlas-note-wrap"),
    hero: document.getElementById("atlas-hero"),
  };

  const state = {
    year: YEAR_MAX_GLOBAL,
    era: "all",
    rangeMin: YEAR_MIN_GLOBAL,
    rangeMax: YEAR_MAX_GLOBAL,
    activeId: null,
    dragging: false,
  };

  // Geographic frame for world-land.png (padded art, no Antarctica strip)
  const MAP_FRAME = {
    x0: 8.9,
    x1: 90.2,
    y0: 9.8,
    y1: 84.5,
    latMax: 85,
    latMin: -56,
  };

  function project(lat, lng) {
    const x =
      MAP_FRAME.x0 +
      ((lng + 180) / 360) * (MAP_FRAME.x1 - MAP_FRAME.x0);
    const y =
      MAP_FRAME.y0 +
      ((MAP_FRAME.latMax - lat) / (MAP_FRAME.latMax - MAP_FRAME.latMin)) *
        (MAP_FRAME.y1 - MAP_FRAME.y0);
    let yOut = y;
    if (lat < -62) {
      yOut = Math.min(y, MAP_FRAME.y1 - 7);
    }
    return {
      x: Math.min(97, Math.max(3, x)),
      y: Math.min(94, Math.max(4, yOut)),
    };
  }

  function clampYear(year) {
    return Math.min(state.rangeMax, Math.max(state.rangeMin, year));
  }

  function defaultYearForEra(eraId) {
    if (eraId === "all") return OPEN_YEAR;
    const range = ERA_RANGES[eraId] || ERA_RANGES.all;
    return range.max;
  }

  function layoutPositions(visible) {
    const groups = new Map();

    visible.forEach(function (entry) {
      const base = project(entry.lat, entry.lng);
      const key =
        Math.round(base.x / CLUSTER_CELL) + ":" + Math.round(base.y / CLUSTER_CELL);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ entry: entry, base: base });
    });

    const placed = [];
    groups.forEach(function (items) {
      if (items.length === 1) {
        placed.push({
          entry: items[0].entry,
          x: items[0].base.x,
          y: items[0].base.y,
        });
        return;
      }

      items.sort(function (a, b) {
        return a.entry.year - b.entry.year || a.entry.name.localeCompare(b.entry.name);
      });

      const cx =
        items.reduce(function (s, it) {
          return s + it.base.x;
        }, 0) / items.length;
      const cy =
        items.reduce(function (s, it) {
          return s + it.base.y;
        }, 0) / items.length;
      const n = items.length;
      const radius = CLUSTER_RADIUS + Math.min(2.2, (n - 1) * 0.42);

      items.forEach(function (it, i) {
        const angle = -Math.PI / 2 + (i / n) * Math.PI * 2;
        placed.push({
          entry: it.entry,
          x: Math.min(98, Math.max(2, cx + Math.cos(angle) * radius)),
          y: Math.min(96, Math.max(4, cy + Math.sin(angle) * radius * 0.72)),
        });
      });
    });

    return placed;
  }

  function initials(name) {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function inEra(entry) {
    return entry.year >= state.rangeMin && entry.year <= state.rangeMax;
  }

  function visibleByYear(entry) {
    return entry.year <= state.year;
  }

  function nearScrubber(entry) {
    return Math.abs(entry.year - state.year) <= WINDOW;
  }

  function yearToPct(year) {
    const span = Math.max(1, state.rangeMax - state.rangeMin);
    return ((year - state.rangeMin) / span) * 100;
  }

  function pctToYear(pct) {
    const clamped = Math.min(100, Math.max(0, pct));
    const span = Math.max(1, state.rangeMax - state.rangeMin);
    return Math.round(state.rangeMin + (clamped / 100) * span);
  }

  function setHandlePosition() {
    const pct = yearToPct(state.year);
    els.handle.style.left = pct + "%";
    if (els.noteWrap) {
      els.noteWrap.style.left = pct + "%";
      if (pct < 20) els.noteWrap.dataset.edge = "start";
      else if (pct > 80) els.noteWrap.dataset.edge = "end";
      else els.noteWrap.dataset.edge = "center";
    }
    els.track.setAttribute("aria-valuemin", String(state.rangeMin));
    els.track.setAttribute("aria-valuemax", String(state.rangeMax));
    els.track.setAttribute("aria-valuenow", String(state.year));
    els.yearReadout.textContent = String(state.year);
    if (els.rangeMin) els.rangeMin.textContent = String(state.rangeMin);
    if (els.rangeMax) els.rangeMax.textContent = String(state.rangeMax);
  }

  function isCompactCard() {
    return window.matchMedia("(max-width: 720px)").matches;
  }

  function positionCardNearMarker(markerEl) {
    if (!els.stage || !markerEl) return;

    if (isCompactCard()) {
      els.card.style.left = "";
      els.card.style.right = "";
      els.card.style.top = "";
      els.card.style.bottom = "";
      return;
    }

    const stageRect = els.stage.getBoundingClientRect();
    const markerRect = markerEl.getBoundingClientRect();
    const cardRect = els.card.getBoundingClientRect();
    const gap = 14;
    const pad = 16;
    const topSafe = 120; // below logo / H1 / eras
    const bottomSafe = 130; // above Dumpling scrubber

    let left = markerRect.right - stageRect.left + gap;
    let top = markerRect.top - stageRect.top - 8;

    // Nudge cards away from the logo when the pin sits in the top-left.
    if (markerRect.left - stageRect.left < stageRect.width * 0.28) {
      left = Math.max(pad, markerRect.right - stageRect.left + gap);
      if (left < pad + 80) {
        left = markerRect.left - stageRect.left + gap;
        top = markerRect.bottom - stageRect.top + gap;
      }
    }

    if (left + cardRect.width > stageRect.width - pad) {
      left = markerRect.left - stageRect.left - cardRect.width - gap;
    }
    if (left < pad) left = pad;

    if (top < topSafe) top = topSafe;
    if (top + cardRect.height > stageRect.height - bottomSafe) {
      top = Math.max(
        topSafe,
        markerRect.top - stageRect.top - cardRect.height - gap
      );
    }
    if (top + cardRect.height > stageRect.height - bottomSafe) {
      top = Math.max(topSafe, stageRect.height - bottomSafe - cardRect.height);
    }

    els.card.style.left = Math.round(left) + "px";
    els.card.style.top = Math.round(top) + "px";
    els.card.style.right = "auto";
    els.card.style.bottom = "auto";
  }

  const FACE_URL_BY_ID = Object.create(null);

  function indexFaceAssets() {
    document.querySelectorAll("#atlas-face-assets img[data-face-id]").forEach(function (img) {
      const id = img.getAttribute("data-face-id");
      // Parcel rewrites this attribute to a hashed root path like /andrew.abc123.png
      const src = img.getAttribute("src");
      if (id && src) FACE_URL_BY_ID[id] = src;
    });
  }

  function preloadFace(src) {
    if (!src || preloadFace.seen.has(src)) return;
    preloadFace.seen.add(src);
    const img = new Image();
    img.decoding = "async";
    img.src = src;
  }
  preloadFace.seen = new Set();

  function faceSrc(entry) {
    if (FACE_URL_BY_ID[entry.id]) return FACE_URL_BY_ID[entry.id];
    if (ATLAS_FACES[entry.id]) return ATLAS_FACES[entry.id];
    return entry.face || "";
  }

  function paintFace(faceWrap, entry) {
    const src = faceSrc(entry);
    if (!src) {
      const span = document.createElement("span");
      span.className = "initials";
      span.textContent = initials(entry.name);
      faceWrap.appendChild(span);
      return;
    }

    preloadFace(src);
    faceWrap.style.backgroundImage = 'url("' + src.replace(/"/g, '\\"') + '")';

    const img = document.createElement("img");
    img.src = src;
    img.alt = "";
    img.loading = "eager";
    img.decoding = "async";
    img.onerror = function () {
      img.remove();
      faceWrap.style.backgroundImage = "";
      faceWrap.classList.remove("is-cutout");
      if (!faceWrap.querySelector(".initials")) {
        const span = document.createElement("span");
        span.className = "initials";
        span.textContent = initials(entry.name);
        faceWrap.appendChild(span);
      }
    };
    faceWrap.appendChild(img);
  }

  function createMarker(item) {
    const entry = item.entry;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "atlas-marker";
    btn.dataset.id = entry.id;
    btn.setAttribute(
      "aria-label",
      entry.name + ", " + entry.year + ", " + entry.place
    );

    const faceWrap = document.createElement("span");
    faceWrap.className = "atlas-marker-face";
    if (ATLAS_CUTOUT_IDS.has(entry.id)) faceWrap.classList.add("is-cutout");
    paintFace(faceWrap, entry);

    const tip = document.createElement("span");
    tip.className = "atlas-marker-tip";
    tip.setAttribute("aria-hidden", "true");
    tip.innerHTML =
      "<strong>" +
      escapeHtml(entry.name) +
      "</strong><span>" +
      escapeHtml(entry.place) +
      (entry.year ? " · " + entry.year : "") +
      "</span>";

    btn.appendChild(faceWrap);
    btn.appendChild(tip);

    btn.addEventListener("click", function (e) {
      e.stopPropagation();
      dismissIntro();
      openCard(entry);
    });

    return btn;
  }

  function syncMarker(btn, item) {
    const entry = item.entry;
    btn.style.left = item.x + "%";
    btn.style.top = item.y + "%";
    btn.classList.toggle("is-active", state.activeId === entry.id);
    btn.classList.toggle(
      "is-near",
      state.activeId !== entry.id && nearScrubber(entry)
    );
  }

  function openCard(entry) {
    state.activeId = entry.id;
    els.cardYear.textContent = String(entry.year);
    els.cardName.textContent = entry.name;
    els.cardPlace.textContent = entry.place;
    els.cardBlurb.textContent = entry.blurb;
    els.cardLink.href = entry.url;
    els.card.hidden = false;
    els.card.classList.add("is-open");
    renderMarkers();

    // Always use the fresh marker after re-render (old node is detached).
    const activeMarker = els.markers.querySelector(
      '.atlas-marker[data-id="' + entry.id + '"]'
    );
    requestAnimationFrame(function () {
      positionCardNearMarker(activeMarker);
    });
  }

  function closeCard() {
    state.activeId = null;
    els.card.classList.remove("is-open");
    window.setTimeout(function () {
      if (!state.activeId) {
        els.card.hidden = true;
        els.card.style.left = "";
        els.card.style.right = "";
        els.card.style.top = "";
        els.card.style.bottom = "";
      }
    }, 200);
    renderMarkers();
  }

  function dismissNote() {
    if (!els.note || els.note.classList.contains("is-dismissed")) return;
    els.note.classList.add("is-dismissed");
  }

  function dismissHero() {
    if (!els.hero || els.hero.classList.contains("is-dismissed")) return;
    els.hero.classList.add("is-dismissed");
  }

  function dismissIntro() {
    dismissNote();
    dismissHero();
  }

  function renderMarkers() {
    const visible = entries.filter(function (entry) {
      return inEra(entry) && visibleByYear(entry);
    });
    const placed = layoutPositions(visible);
    const nextIds = new Set(placed.map(function (item) {
      return item.entry.id;
    }));
    const existing = new Map();

    els.markers.querySelectorAll(".atlas-marker").forEach(function (btn) {
      const id = btn.dataset.id;
      if (!nextIds.has(id)) {
        btn.remove();
        return;
      }
      existing.set(id, btn);
    });

    placed.forEach(function (item) {
      let btn = existing.get(item.entry.id);
      if (!btn) {
        btn = createMarker(item);
        els.markers.appendChild(btn);
      }
      syncMarker(btn, item);
    });

    els.count.textContent =
      visible.length + (visible.length === 1 ? " story" : " stories");

    if (els.empty) {
      els.empty.hidden = visible.length !== 0;
    }

    if (state.activeId) {
      const activeMarker = els.markers.querySelector(
        '.atlas-marker[data-id="' + state.activeId + '"]'
      );
      if (activeMarker) {
        requestAnimationFrame(function () {
          positionCardNearMarker(activeMarker);
        });
      }
    }
  }

  function buildTicks() {
    const majors = [1900, 1950, 2000];
    const frag = document.createDocumentFragment();
    const start = Math.ceil(state.rangeMin / 10) * 10;
    for (let y = start; y <= state.rangeMax; y += 10) {
      const tick = document.createElement("span");
      tick.className = "atlas-tick" + (majors.indexOf(y) !== -1 ? " is-major" : "");
      tick.style.left = yearToPct(y) + "%";
      frag.appendChild(tick);
    }
    els.ticks.replaceChildren(frag);
  }

  function setYear(year, opts) {
    const next = Math.min(state.rangeMax, Math.max(state.rangeMin, year));
    if (next === state.year && !(opts && opts.force)) return;
    const prev = state.year;
    state.year = next;
    setHandlePosition();
    if (next !== prev && els.yearReadout) {
      els.yearReadout.classList.remove("is-tick");
      void els.yearReadout.offsetWidth;
      els.yearReadout.classList.add("is-tick");
    }
    renderMarkers();
  }

  function applyEra(eraId, opts) {
    const range = ERA_RANGES[eraId] || ERA_RANGES.all;
    state.era = eraId;
    state.rangeMin = range.min;
    state.rangeMax = range.max;
    buildTicks();
    if (opts && opts.keepYear) {
      setYear(clampYear(state.year), { force: true });
    } else {
      setYear(clampYear(defaultYearForEra(eraId)), { force: true });
    }
  }

  function pointerPct(clientX) {
    const rect = els.track.getBoundingClientRect();
    return ((clientX - rect.left) / rect.width) * 100;
  }

  function onPointerDown(e) {
    state.dragging = true;
    els.handle.classList.add("is-dragging");
    els.track.setPointerCapture?.(e.pointerId);
    dismissIntro();
    setYear(pctToYear(pointerPct(e.clientX)));
  }

  function onPointerMove(e) {
    if (!state.dragging) return;
    setYear(pctToYear(pointerPct(e.clientX)));
  }

  function onPointerUp(e) {
    if (!state.dragging) return;
    state.dragging = false;
    els.handle.classList.remove("is-dragging");
    try {
      els.track.releasePointerCapture?.(e.pointerId);
    } catch (_) {}
  }

  els.track.addEventListener("pointerdown", onPointerDown);
  els.track.addEventListener("pointermove", onPointerMove);
  els.track.addEventListener("pointerup", onPointerUp);
  els.track.addEventListener("pointercancel", onPointerUp);

  els.track.addEventListener("keydown", function (e) {
    const step = e.shiftKey ? 10 : 1;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      setYear(state.year - step);
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      setYear(state.year + step);
    } else if (e.key === "Home") {
      e.preventDefault();
      setYear(state.rangeMin);
    } else if (e.key === "End") {
      e.preventDefault();
      setYear(state.rangeMax);
    }
  });

  els.eras.forEach(function (btn) {
    btn.addEventListener("click", function () {
      els.eras.forEach(function (b) {
        const on = b === btn;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-selected", on ? "true" : "false");
      });
      closeCard();
      dismissIntro();
      applyEra(btn.dataset.era);
    });
  });

  els.cardClose.addEventListener("click", closeCard);
  els.stage.addEventListener("click", function (e) {
    if (e.target.closest(".atlas-marker") || e.target.closest(".atlas-card")) return;
    closeCard();
  });

  window.addEventListener("resize", function () {
    if (!state.activeId) return;
    const activeMarker = els.markers.querySelector(
      '.atlas-marker[data-id="' + state.activeId + '"]'
    );
    positionCardNearMarker(activeMarker);
  });

  indexFaceAssets();
  entries.forEach(function (entry) {
    preloadFace(faceSrc(entry));
  });
  applyEra("all");
})();
